import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

const toolbarBtn = {
  padding: "6px 12px",
  fontSize: "0.85rem",
  borderRadius: "6px",
  border: "1px solid rgba(255,255,255,0.25)",
  background: "rgba(255,255,255,0.1)",
  color: "#f1f5f9",
  cursor: "pointer",
};

/**
 * Full-screen overlay for article images (raster or SVG when rendered as img).
 * Scroll to pan when zoomed; buttons adjust zoom; Esc closes.
 */
export function ArticleImageLightbox({ open, src, alt, onClose }) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (open) setZoom(1);
  }, [open, src]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !src) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged image"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "rgba(15, 23, 42, 0.92)",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={onClose}
    >
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "10px 12px",
          background: "rgba(0,0,0,0.35)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ color: "#cbd5e1", fontSize: "0.8rem" }}>
          Scroll to pan · +/− to zoom · Esc to close
        </span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <button
            type="button"
            style={toolbarBtn}
            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
            aria-label="Zoom out"
          >
            −
          </button>
          <span style={{ color: "#94a3b8", fontSize: "0.8rem", minWidth: "3.5rem", textAlign: "center" }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            style={toolbarBtn}
            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
            aria-label="Zoom in"
          >
            +
          </button>
          <button type="button" style={toolbarBtn} onClick={() => setZoom(1)} aria-label="Reset zoom">
            Reset
          </button>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...toolbarBtn, textDecoration: "none", display: "inline-block" }}
          >
            Open original
          </a>
          <button type="button" style={toolbarBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: 16,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          WebkitOverflowScrolling: "touch",
        }}
        onClick={onClose}
      >
        <div onClick={(e) => e.stopPropagation()} role="presentation">
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "top center",
              transition: "transform 0.15s ease-out",
            }}
          >
            {/* Natural/intrinsic size so diagrams stay readable; outer area scrolls */}
            <img
              src={src}
              alt={alt || ""}
              style={{
                maxWidth: "none",
                width: "auto",
                height: "auto",
                display: "block",
                borderRadius: 4,
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
